import type { AccessGuardMetricsEntry, ResourceEvent } from '@/lib/urm-api';
import { latestWriteBlocks, MAX_RESOURCE_EVENT_SAMPLES, WRITE_BLOCK_REASON, WRITE_BLOCK_WINDOW_MS } from '@/lib/urm-api';

export interface AccessGuardDimensions {
  user: string | null;
  path: string | null;
  group: string | null;
  version: string | null;
  type: string | null;
  name: string | null;
  recognized: boolean;
}

export interface AccessGuardRow extends AccessGuardMetricsEntry {
  dimensions: AccessGuardDimensions;
}

export interface AccessGuardUserSummary {
  user: string | null;
  count: number;
}

export type AccessGuardSortField = 'count' | 'action' | 'user' | 'type' | 'name';
export type SortDirection = 'asc' | 'desc';

const emptyDimensions = (): AccessGuardDimensions => ({
  user: null,
  path: null,
  group: null,
  version: null,
  type: null,
  name: null,
  recognized: false,
});

function normalizeLimiterResourceType(type: string): string {
  const [mixin, host, ...duplicates] = type.split('@');
  // AccessGuard can append the host to an already concrete mixin type.
  return host && duplicates.length && duplicates.every((value) => value === host)
    ? `${mixin}@${host}` : type;
}

// Keep this in sync with resources-server's getLimiterKey(): getEvents is
// user-scoped, touch is resource-scoped, and the remaining guards combine both.
export function parseAccessGuardLimiterKey(action: string, limiterKey: string): AccessGuardDimensions {
  if (action === 'getEvents' && limiterKey) {
    return { ...emptyDimensions(), user: limiterKey, recognized: true };
  }

  const parts = limiterKey.split(':');
  if (action === 'touch' && parts.length === 5 && parts.every(Boolean)) {
    const [path, group, version, type, name] = parts;
    return { user: null, path, group, version, type: normalizeLimiterResourceType(type), name, recognized: true };
  }

  if (['read', 'write', 'getHistory'].includes(action)
    && parts.length === 6
    && parts.every(Boolean)) {
    const [user, path, group, version, type, name] = parts;
    return { user, path, group, version, type: normalizeLimiterResourceType(type), name, recognized: true };
  }

  return emptyDimensions();
}

export function buildAccessGuardRows(entries: AccessGuardMetricsEntry[]): AccessGuardRow[] {
  return entries.map((entry) => ({
    ...entry,
    dimensions: parseAccessGuardLimiterKey(entry.action, entry.limiter_key),
  }));
}

function sortValue(row: AccessGuardRow, field: AccessGuardSortField): string | number {
  if (field === 'count') return row.count;
  if (field === 'action') return row.action;
  return row.dimensions[field] ?? '';
}

export function sortAccessGuardRows(
  rows: AccessGuardRow[],
  field: AccessGuardSortField,
  direction: SortDirection,
): AccessGuardRow[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    const leftValue = sortValue(left, field);
    const rightValue = sortValue(right, field);
    const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
    return comparison * multiplier
      || left.action.localeCompare(right.action)
      || left.limiter_key.localeCompare(right.limiter_key);
  });
}

export function aggregateAccessGuardRowsByUser(rows: AccessGuardRow[]): AccessGuardUserSummary[] {
  const summaries = new Map<string | null, AccessGuardUserSummary>();
  for (const row of rows) {
    const key = row.dimensions.user;
    const existing = summaries.get(key);
    if (existing) {
      existing.count += row.count;
    } else {
      summaries.set(key, {
        user: row.dimensions.user,
        count: row.count,
      });
    }
  }
  return Array.from(summaries.values());
}

function escapeCsvCell(value: string | number): string {
  let text = String(value);
  // Prevent spreadsheet applications from interpreting exported data as a formula.
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

function resourceLabel(row: AccessGuardRow): string {
  const { dimensions } = row;
  if (!dimensions.recognized) return row.limiter_key;
  if (!dimensions.type) return 'All resources';
  return `${dimensions.type} / ${dimensions.name} (${dimensions.group}/${dimensions.version}) · ${dimensions.path}`;
}

export function accessGuardRowsToCsv(rows: AccessGuardRow[]): string {
  const records: Array<Array<string | number>> = [
    ['Action', 'User', 'Resource', 'Count'],
    ...rows.map((row) => [
      row.action,
      row.dimensions.user ?? '',
      resourceLabel(row),
      row.count,
    ]),
  ];
  return `\uFEFF${records.map((record) => record.map(escapeCsvCell).join(',')).join('\r\n')}\r\n`;
}

export interface EventBurstStats {
  count: number;
  firstTime: string | null;
  lastTime: string | null;
  estimatedRps: number | null;
}

export interface EventStats {
  count: number;
  oneMinute: EventBurstStats;
  activeBurst: { estimatedEps: number | null; burstCount: number; peak?: EventBurstStats };
}

export interface EventUserSummary extends EventStats {
  user: string;
  preBlock?: { blockedAt: string; windowStart: string; complete: boolean };
  messages: Array<EventStats & { message: string }>;
}

export interface WriteInvestigation {
  users: EventUserSummary[];
  sequence: UpdateSequenceStep[];
  count: number;
  error?: string;
}

export interface UpdateSequenceStep {
  user: string;
  message: string;
  count: number;
  firstTime: string;
  lastTime: string;
}

function isIncludedUpdate(event: ResourceEvent): boolean {
  return event.action === 'update' && event.reason !== 'TriggerWebhookCall' && event.reason !== WRITE_BLOCK_REASON;
}

export function buildUpdateSequence(events: ResourceEvent[]): UpdateSequenceStep[] {
  const ordered = events.filter((event) => isIncludedUpdate(event)
    && Number.isFinite(Date.parse(event.time ?? '')) && Date.parse(event.time ?? '') > 0)
    .sort((a, b) => Date.parse(a.time!) - Date.parse(b.time!));
  const sequence: UpdateSequenceStep[] = [];
  for (const event of ordered) {
    const user = event.actor || 'Unknown user';
    const message = event.message || '(No message)';
    const time = new Date(event.time!).toISOString();
    const previous = sequence.at(-1);
    if (previous?.user === user && previous.message === message
      && Date.parse(time) - Date.parse(previous.lastTime) <= 60_000) {
      previous.count += 1;
      previous.lastTime = time;
    } else {
      sequence.push({ user, message, count: 1, firstTime: time, lastTime: time });
    }
  }
  return sequence;
}

export interface UpdateCycle {
  users: string[];
  repetitions: number;
  typicalRoundTripSeconds: number | null;
  sharedFields: string[];
  steps: UpdateSequenceStep[];
}

export function findUpdateCycle(sequence: UpdateSequenceStep[]): UpdateCycle | null {
  // shortcut: inspect the latest 10,000 steps; use a linear cycle detector for larger sequences.
  sequence = sequence.slice(-MAX_RESOURCE_EVENT_SAMPLES);
  const segments: Array<Array<{ user: string; start: number; end: number }>> = [];
  let segment: typeof segments[number] = [];
  for (let index = 0; index < sequence.length; index += 1) {
    const step = sequence[index];
    const gap = index === 0 ? 0 : Date.parse(step.firstTime) - Date.parse(sequence[index - 1].lastTime);
    if (gap > 60_000 || step.user === 'Unknown user' || sequence[index - 1]?.user === 'Unknown user') {
      if (segment.length) segments.push(segment);
      segment = [];
    }
    if (step.user === 'Unknown user') continue;
    const previous = segment.at(-1);
    if (previous?.user === step.user) previous.end = index;
    else segment.push({ user: step.user, start: index, end: index });
  }
  if (segment.length) segments.push(segment);

  let best: { runs: typeof segment; period: number; start: number; length: number } | null = null;
  // Compare each possible period once; three full repetitions are required.
  for (const runs of segments) {
    for (let period = 2; period <= Math.floor(runs.length / 3); period += 1) {
      let matches = 0;
      for (let index = period; index < runs.length; index += 1) {
        matches = runs[index].user === runs[index - period].user ? matches + 1 : 0;
        if (matches < period * 2) continue;
        const length = Math.floor((matches + period) / period) * period;
        if (!best || length > best.length || (length === best.length && period < best.period)) {
          best = { runs, period, start: index - matches - period + 1, length };
        }
      }
    }
  }
  if (!best) return null;
  const { runs, period, start, length } = best;
  const cycleRuns = runs.slice(start, start + length);
  const users = cycleRuns.slice(0, period).map((run) => run.user);
  const steps = sequence.slice(cycleRuns[0].start, cycleRuns.at(-1)!.end + 1);
  const roundTrips = cycleRuns.slice(period).map((run, index) =>
    (Date.parse(sequence[run.start].firstTime) - Date.parse(sequence[cycleRuns[index].start].firstTime)) / 1000)
    .sort((a, b) => a - b);
  const middle = Math.floor(roundTrips.length / 2);
  const median = roundTrips.length % 2 ? roundTrips[middle] : (roundTrips[middle - 1] + roundTrips[middle]) / 2;
  const fieldsByUser = [...new Set(users)].map((user) => new Set(steps.filter((step) => step.user === user)
    .flatMap((step) => [...step.message.matchAll(/'(\.[^']+)'/g)].map((match) => match[1]))));
  return {
    users, repetitions: length / period, steps,
    typicalRoundTripSeconds: median > 0 ? median : null,
    sharedFields: [...fieldsByUser[0]].filter((field) => fieldsByUser.every((fields) => fields.has(field))),
  };
}

export function writeInvestigationToText(row: AccessGuardRow, result: WriteInvestigation): string {
  const { user, group, version, type, name, path } = row.dimensions;
  const cycle = findUpdateCycle(result.sequence);
  const rate = (value: number | null) => value === null ? 'Unavailable'
    : '≈' + value.toLocaleString('en-US', { maximumSignificantDigits: 3 });
  const lines = [
    'Access Guard WRITE investigation',
    `Violation user: ${user ?? 'Unknown user'}`,
    `Resource group: ${group}`,
    `Resource version: ${version}`,
    `Resource type: ${type}`,
    `Resource name: ${name}`,
    `Resource path: ${path}`,
    '',
    '----------',
    '',
    'Event breakdown (only significant findings):',
  ];
  let hasMessages = false;
  for (const entry of result.users) {
    const messages = entry.messages.filter((message) => message.count >= 50);
    if (!messages.length) continue;
    if (hasMessages) lines.push('---', '');
    hasMessages = true;
    lines.push(`User: ${entry.user}`);
    if (entry.preBlock) {
      lines.push(`WRITE blocked (UTC): ${entry.preBlock.blockedAt}`,
        `Estimate window (UTC): ${entry.preBlock.windowStart} – ${entry.preBlock.blockedAt}`);
      if (!entry.preBlock.complete) lines.push('Pre-block estimate unavailable: incomplete window.');
    }
    if (entry.activeBurst.estimatedEps !== null) {
      lines.push(`${entry.preBlock ? 'User pre-block burst pace' : 'User active burst pace'}: ${rate(entry.activeBurst.estimatedEps)} events/s`);
    }
    for (const message of messages) {
      lines.push(`Event: ${message.message}`, '');
    }
  }
  if (!hasMessages) lines.push('No significant findings.', '');
  if (cycle) {
    lines.push('----------', '', 'Suspected update cycle:',
      `Pattern: ${cycle.users.join(' → ')} → ${cycle.users[0]}`,
      `Repetitions: ${cycle.repetitions}`,
      `Typical round-trip: ${cycle.typicalRoundTripSeconds === null ? 'Unavailable' : cycle.typicalRoundTripSeconds.toLocaleString('en-US', { maximumSignificantDigits: 3 }) + ' seconds'}`);
    if (cycle.sharedFields.length) lines.push(`Shared fields: ${cycle.sharedFields.join(', ')}`);
  }
  return lines.join('\n').trimEnd();
}

export function investigationTextToHtml(text: string): string {
  const escape = (value: string) => value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
  return '<div>' + text.split('\n').map((line) => {
    if (line === '----------' || line === '---') return '<hr>';
    if (!line) return '<p><br></p>';
    if (line === 'Access Guard WRITE investigation') return `<h2>${escape(line)}</h2>`;
    if (line === 'Event breakdown (only significant findings):' || line === 'Suspected update cycle:') return `<h3>${escape(line)}</h3>`;
    if (line.startsWith('User: ')) return `<p><strong>${escape(line)}</strong></p>`;
    const colon = line.indexOf(':');
    return colon > 0 ? `<p><strong>${escape(line.slice(0, colon + 1))}</strong>${escape(line.slice(colon + 1))}</p>`
      : `<p>${escape(line)}</p>`;
  }).join('') + '</div>';
}

export function writeResourceKey(row: AccessGuardRow): string | null {
  const { path, group, version, type, name } = row.dimensions;
  return row.action === 'write' && row.dimensions.recognized
    ? JSON.stringify([path, group, version, type, name]) : null;
}

function busiestBurst(validTimes: number[], completeTimes: boolean): EventBurstStats {
  let start = 0;
  let peakStart = 0;
  let peakEnd = -1;
  // Use a sliding window so a burst crossing a clock boundary stays together.
  for (let end = 0; end < validTimes.length; end += 1) {
    while (validTimes[end] - validTimes[start] > 60_000) start += 1;
    const count = end - start + 1;
    const peakCount = peakEnd - peakStart + 1;
    const duration = validTimes[end] - validTimes[start];
    const peakDuration = peakEnd < 0 ? 0 : validTimes[peakEnd] - validTimes[peakStart];
    // Equal-count windows prefer the shorter measurable burst; otherwise keep the earliest.
    if (count > peakCount || (count === peakCount && duration > 0
      && (peakDuration === 0 || duration < peakDuration))) {
      peakStart = start;
      peakEnd = end;
    }
  }
  const peakCount = peakEnd - peakStart + 1;
  const first = peakEnd < 0 ? null : validTimes[peakStart];
  const last = peakEnd < 0 ? null : validTimes[peakEnd];
  return {
    count: peakCount,
    firstTime: first === null ? null : new Date(first).toISOString(),
    lastTime: last === null ? null : new Date(last).toISOString(),
    estimatedRps: first !== null && last !== null && last > first && completeTimes
      ? peakCount / ((last - first) / 1000) : null,
  };
}

function eventStats(events: ResourceEvent[], burstIds: Map<ResourceEvent, number>, blockedAt?: number, completeWindow = true): EventStats {
  const timedEvents = events.filter((event) => burstIds.has(event))
    .sort((a, b) => Date.parse(a.time!) - Date.parse(b.time!));
  const validEvents = blockedAt === undefined ? timedEvents : timedEvents.filter((event) => {
    const time = Date.parse(event.time!);
    return time >= blockedAt - WRITE_BLOCK_WINDOW_MS && time < blockedAt;
  });
  const validTimes = validEvents.map((event) => Date.parse(event.time!));
  const completeTimes = timedEvents.length === events.length && completeWindow;
  const peak = busiestBurst(validTimes, completeTimes);
  const peakEvents = blockedAt === undefined ? validEvents : validEvents.filter((event) =>
    Date.parse(event.time!) >= Date.parse(peak.firstTime ?? '') && Date.parse(event.time!) <= Date.parse(peak.lastTime ?? ''));
  const gaps: number[] = [];
  const contributingBursts = new Set<number>();
  for (let index = 1; index < peakEvents.length; index += 1) {
    const burst = burstIds.get(peakEvents[index])!;
    const gap = Date.parse(peakEvents[index].time!) - Date.parse(peakEvents[index - 1].time!);
    if ((blockedAt !== undefined || burst === burstIds.get(peakEvents[index - 1])) && gap > 0) {
      gaps.push(gap);
      contributingBursts.add(burst);
    }
  }
  gaps.sort((a, b) => a - b);
  const middle = Math.floor(gaps.length / 2);
  const medianGap = gaps.length % 2 ? gaps[middle] : (gaps[middle - 1] + gaps[middle]) / 2;
  return {
    count: events.length,
    oneMinute: peak,
    activeBurst: {
      estimatedEps: gaps.length && completeTimes ? 1000 / medianGap : null,
      burstCount: completeTimes ? (blockedAt === undefined ? contributingBursts.size : Number(gaps.length > 0)) : 0,
      ...(blockedAt === undefined ? {} : { peak }),
    },
  };
}

export function aggregateResourceEvents(events: ResourceEvent[], historyExhausted = false): EventUserSummary[] {
  const blocks = latestWriteBlocks(events);
  const oldestTime = events.reduce((oldest, event) => {
    const time = Date.parse(event.time ?? '');
    return time > 0 ? Math.min(oldest, time) : oldest;
  }, Infinity);
  const updates = events.filter(isIncludedUpdate);
  const timedUpdates = updates.filter((event) => Number.isFinite(Date.parse(event.time ?? '')) && Date.parse(event.time ?? '') > 0)
    .sort((a, b) => Date.parse(a.time!) - Date.parse(b.time!));
  const burstIds = new Map<ResourceEvent, number>();
  let burst = 0;
  for (let index = 0; index < timedUpdates.length; index += 1) {
    if (index > 0 && Date.parse(timedUpdates[index].time!) - Date.parse(timedUpdates[index - 1].time!) > 1000) burst += 1;
    burstIds.set(timedUpdates[index], burst);
  }
  const byUser = new Map<string, ResourceEvent[]>();
  for (const event of updates) {
    const user = event.actor || 'Unknown user';
    const group = byUser.get(user) ?? [];
    group.push(event);
    byUser.set(user, group);
  }
  for (const user of blocks.keys()) if (!byUser.has(user)) byUser.set(user, []);
  return Array.from(byUser, ([user, userEvents]) => {
    const blockedAt = blocks.get(user);
    const completeWindow = blockedAt === undefined || historyExhausted || oldestTime <= blockedAt - WRITE_BLOCK_WINDOW_MS;
    const byMessage = new Map<string, ResourceEvent[]>();
    for (const event of userEvents) {
      const message = event.message || '(No message)';
      const group = byMessage.get(message) ?? [];
      group.push(event);
      byMessage.set(message, group);
    }
    return {
      user, ...eventStats(userEvents, burstIds, blockedAt, completeWindow),
      ...(blockedAt === undefined ? {} : { preBlock: {
        blockedAt: new Date(blockedAt).toISOString(),
        windowStart: new Date(blockedAt - WRITE_BLOCK_WINDOW_MS).toISOString(), complete: completeWindow,
      } }),
      messages: Array.from(byMessage, ([message, messageEvents]) => ({
        message, ...eventStats(messageEvents, burstIds, blockedAt, completeWindow),
      })).sort((a, b) => b.count - a.count || a.message.localeCompare(b.message)),
    };
  }).sort((a, b) => b.count - a.count || a.user.localeCompare(b.user));
}
